/**
 * Carga una instancia DICOM con check-before-fetch en IndexedDB.
 * Hit → fileManager (sin red). Miss/partial → fetch (+ Range) → decode → put async.
 */
import cornerstone from 'cornerstone-core'
import * as cornerstoneWADOImageLoader from 'cornerstone-wado-image-loader'
import dicomParser from 'dicom-parser'
import {
  DICOM_OFFLINE_CACHE_ENABLED,
  getCachedInstance,
  putPartialInstance,
  deleteCachedKey,
  toArrayBuffer,
  concatArrayBuffers,
} from '../db/dicomCache'
import { enqueuePutComplete, getWriteQueueStats } from '../db/dicomCacheWriteQueue'
import {
  sopFromManifestInstance,
  sopFromWadoUrl,
} from './sopFromWadoUrl'

export { sopFromManifestInstance, sopFromWadoUrl }

/**
 * Clave estable para parciales cuando aún no hay SOP.
 * @param {string} absoluteUrl
 * @param {object} [inst]
 */
export function fallbackCacheKey(absoluteUrl, inst) {
  const sop = sopFromManifestInstance(inst)
  if (sop) return sop
  const path =
    (inst && (inst.path || inst.key || inst.url)) != null
      ? String(inst.path || inst.key || inst.url)
      : absoluteUrl || ''
  let h = 0
  const s = path
  for (let i = 0; i < s.length; i++) {
    h = (Math.imul(31, h) + s.charCodeAt(i)) | 0
  }
  return `urlhash:${(h >>> 0).toString(16)}:${s.slice(-48)}`
}

/**
 * Extrae SOPInstanceUID de un Part-10 ArrayBuffer (mejor esfuerzo).
 * @param {ArrayBuffer} buffer
 * @returns {string|null}
 */
export function parseSopFromDicomBuffer(buffer) {
  if (!buffer || !(buffer instanceof ArrayBuffer)) return null
  try {
    const byteArray = new Uint8Array(buffer)
    const dataSet = dicomParser.parseDicom(byteArray, {
      untilTag: 'x00080018',
    })
    const sop = dataSet && dataSet.string('x00080018')
    return sop ? String(sop).trim() : null
  } catch (e) {
    try {
      const byteArray = new Uint8Array(buffer)
      const dataSet = dicomParser.parseDicom(byteArray)
      const sop = dataSet && dataSet.string('x00080018')
      return sop ? String(sop).trim() : null
    } catch (e2) {
      return null
    }
  }
}

function getFileManager() {
  // namespace import o default (según bundler)
  const root =
    cornerstoneWADOImageLoader &&
    (cornerstoneWADOImageLoader.wadouri
      ? cornerstoneWADOImageLoader
      : cornerstoneWADOImageLoader.default || cornerstoneWADOImageLoader)
  const wado = root && root.wadouri
  if (!wado || !wado.fileManager) {
    throw new Error('cornerstoneWADOImageLoader.wadouri.fileManager no disponible')
  }
  return wado.fileManager
}

/**
 * @param {ArrayBuffer} buffer
 * @returns {string} imageId
 */
export function imageIdFromBuffer(buffer) {
  const fm = getFileManager()
  if (typeof fm.addBuffer === 'function') {
    return fm.addBuffer(buffer)
  }
  const blob = new Blob([buffer], { type: 'application/dicom' })
  return fm.add(blob)
}

/**
 * Fetch con soporte Range y fusión de parciales.
 * @param {string} url
 * @param {{
 *   existingBuffer?: ArrayBuffer|null,
 *   expectedByteLength?: number|null,
 *   headers?: Record<string, string>,
 *   credentials?: RequestCredentials,
 * }} [opts]
 * @returns {Promise<{ buffer: ArrayBuffer, complete: boolean, expectedByteLength: number|null, fromNetwork: boolean }>}
 */
export async function fetchDicomBytes(url, opts = {}) {
  const existing = opts.existingBuffer || null
  const existingLen = existing ? existing.byteLength : 0
  const headers = { ...(opts.headers || {}) }
  if (existingLen > 0) {
    headers.Range = `bytes=${existingLen}-`
  }

  const res = await fetch(url, {
    method: 'GET',
    credentials: opts.credentials || 'omit',
    headers,
  })

  if (!res.ok && res.status !== 206) {
    throw new Error(`HTTP ${res.status} al descargar DICOM`)
  }

  const contentRange = res.headers.get('Content-Range')
  const contentLength = res.headers.get('Content-Length')
  let expected = opts.expectedByteLength != null ? opts.expectedByteLength : null

  if (contentRange) {
    // bytes 100-199/500
    const m = /\/(\d+)\s*$/.exec(contentRange)
    if (m) expected = parseInt(m[1], 10)
  } else if (contentLength && res.status === 200) {
    expected = parseInt(contentLength, 10)
  } else if (contentLength && res.status === 206 && existingLen > 0) {
    const part = parseInt(contentLength, 10)
    if (Number.isFinite(part)) expected = existingLen + part
  }

  const chunk = await res.arrayBuffer()
  let buffer
  if (res.status === 206 && existing && existingLen > 0) {
    buffer = concatArrayBuffers(existing, chunk)
  } else if (res.status === 200) {
    // Full body (servidor ignoró Range o petición completa)
    buffer = chunk
  } else {
    buffer = existing && existingLen > 0 ? concatArrayBuffers(existing, chunk) : chunk
  }

  const complete =
    expected != null && Number.isFinite(expected)
      ? buffer.byteLength >= expected
      : true // sin Content-Length: tratamos respuesta como completa

  return {
    buffer,
    complete,
    expectedByteLength: expected,
    fromNetwork: true,
  }
}

/**
 * Persistencia en background vía cola serial (no await del caller).
 * Clona a Blob en la cola; el buffer del caller puede liberarse.
 * Nunca debe bloquear medición si la cuota IDB está llena.
 */
function schedulePutComplete(sop, studyUid, buffer, skipWrite) {
  if (skipWrite || !DICOM_OFFLINE_CACHE_ENABLED || !sop || !buffer) return
  try {
    enqueuePutComplete({
      sopInstanceUID: sop,
      studyInstanceUID: studyUid,
      blob: buffer,
      expectedByteLength: buffer.byteLength,
    })
  } catch (e) {
    /* ignore – cache write is optional */
  }
}

/**
 * Decode buffer → cornerstone image; purga imageCache ante presión de memoria.
 * @param {ArrayBuffer} buffer
 * @returns {Promise<{ image: object, imageId: string }>}
 */
async function decodeDicomBufferToImage(buffer) {
  const imageId = imageIdFromBuffer(buffer)
  try {
    const image = await cornerstone.loadAndCacheImage(imageId)
    return { image, imageId }
  } catch (e) {
    // Reintento tras purgar caché de canvas (imágenes grandes + previews)
    try {
      if (cornerstone.imageCache && typeof cornerstone.imageCache.purgeCache === 'function') {
        cornerstone.imageCache.purgeCache()
      }
    } catch (e2) {
      /* ignore */
    }
    const image = await cornerstone.loadAndCacheImage(imageId)
    return { image, imageId }
  }
}

/**
 * Carga desde IDB + fileManager por SOP (rehidratación viewport).
 * @param {string} sopInstanceUID
 * @param {string} [studyInstanceUID]
 * @returns {Promise<{ image: object, imageId: string, sopInstanceUID: string, fromCache: boolean }|null>}
 */
export async function loadFromCacheBySop(sopInstanceUID, studyInstanceUID) {
  if (!DICOM_OFFLINE_CACHE_ENABLED || !sopInstanceUID) return null
  try {
    const row = await getCachedInstance(String(sopInstanceUID))
    if (!row || row.status !== 'complete' || !row.blob) return null
    const buffer = await toArrayBuffer(row.blob)
    if (!buffer || buffer.byteLength === 0) return null
    const { image, imageId } = await decodeDicomBufferToImage(buffer)
    return {
      image,
      imageId,
      sopInstanceUID: String(sopInstanceUID),
      fromCache: true,
    }
  } catch (e) {
    console.warn('loadFromCacheBySop failed', e)
    return null
  }
}

export { getWriteQueueStats }

/**
 * Extrae Authorization de un objeto header tipo { Authorization: 'Basic …' }
 * o string "Basic …".
 * @param {Record<string, string>|Headers|string|null|undefined} auth
 * @returns {Record<string, string>}
 */
export function normalizeFetchHeaders(auth) {
  if (!auth) return {}
  if (typeof auth === 'string') {
    const s = auth.trim()
    if (!s) return {}
    if (/^basic\s/i.test(s) || /^bearer\s/i.test(s)) {
      return { Authorization: s }
    }
    return { Authorization: s }
  }
  if (typeof Headers !== 'undefined' && auth instanceof Headers) {
    const out = {}
    auth.forEach((v, k) => {
      out[k] = v
    })
    return out
  }
  if (typeof auth === 'object') {
    const out = {}
    Object.keys(auth).forEach((k) => {
      if (auth[k] != null) out[k] = String(auth[k])
    })
    return out
  }
  return {}
}

/**
 * @param {object} params
 * @param {string} params.absoluteUrl
 * @param {object} [params.inst]
 * @param {string} [params.studyInstanceUID]
 * @param {string} [params.sopInstanceUID] — Orthanc: SOP confiable sin nested tags
 * @param {Record<string, string>|Headers|string} [params.fetchHeaders] — Basic para WADO Orthanc
 * @param {RequestCredentials} [params.credentials]
 * @param {boolean} [params.skipIdbRead] — no leer IDB (útil tras cuota)
 * @param {boolean} [params.skipIdbWrite] — no encolar put (upgrade on-demand)
 * @returns {Promise<{ image: object, imageId: string, sopInstanceUID: string, fromCache: boolean }>}
 */
export async function loadDicomInstanceWithCache({
  absoluteUrl,
  inst,
  studyInstanceUID,
  sopInstanceUID,
  fetchHeaders,
  credentials,
  skipIdbRead = false,
  skipIdbWrite = false,
}) {
  const studyUid = String(studyInstanceUID || '')
  const instNorm =
    inst && typeof inst === 'object'
      ? {
          ...inst,
          sopInstanceUID:
            sopInstanceUID ||
            inst.sopInstanceUID ||
            inst.SOPInstanceUID ||
            (inst.MainDicomTags && inst.MainDicomTags.SOPInstanceUID) ||
            null,
        }
      : sopInstanceUID
        ? { sopInstanceUID }
        : {}

  const manifestSop = sopInstanceUID
    ? String(sopInstanceUID).trim()
    : sopFromManifestInstance(instNorm)
  const cacheKey = fallbackCacheKey(absoluteUrl, instNorm)
  const extraHeaders = normalizeFetchHeaders(fetchHeaders)

  let fromCache = false
  let buffer = null
  let existingPartial = null
  let expectedByteLength = null

  if (DICOM_OFFLINE_CACHE_ENABLED && !skipIdbRead) {
    try {
      // Preferir SOP del manifest/Orthanc; también intentar clave fallback
      const keysToTry = []
      if (manifestSop) keysToTry.push(manifestSop)
      if (cacheKey && !keysToTry.includes(cacheKey)) keysToTry.push(cacheKey)

      for (const key of keysToTry) {
        const row = await getCachedInstance(key)
        if (!row) continue
        if (row.status === 'complete' && row.blob) {
          try {
            buffer = await toArrayBuffer(row.blob)
            if (buffer && buffer.byteLength > 0) {
              fromCache = true
              break
            }
          } catch (e) {
            buffer = null
            fromCache = false
          }
        }
        if (row.status === 'partial' && row.blob) {
          try {
            existingPartial = await toArrayBuffer(row.blob)
            expectedByteLength = row.expectedByteLength
          } catch (e) {
            existingPartial = null
          }
          break
        }
      }
    } catch (e) {
      // IDB roto / cuota → red
      buffer = null
      fromCache = false
      existingPartial = null
    }
  }

  if (!buffer) {
    const fetched = await fetchDicomBytes(absoluteUrl, {
      existingBuffer: existingPartial,
      expectedByteLength,
      headers: extraHeaders,
      credentials: credentials || 'omit',
    })
    buffer = fetched.buffer
    fromCache = false

    if (!fetched.complete && buffer && buffer.byteLength > 0) {
      // Guardar parcial opcional; no bloquear
      if (!skipIdbWrite) {
        const partialKey = manifestSop || cacheKey
        void putPartialInstance({
          sopInstanceUID: partialKey,
          studyInstanceUID: studyUid,
          blob: buffer,
          expectedByteLength: fetched.expectedByteLength,
        }).catch(() => {})
      }
      throw new Error(
        `Descarga incompleta (${buffer.byteLength}/${fetched.expectedByteLength || '?'} bytes)`
      )
    }
  }

  if (!buffer || buffer.byteLength === 0) {
    throw new Error('Buffer DICOM vacío')
  }

  let sop = manifestSop || parseSopFromDicomBuffer(buffer) || cacheKey

  // Decode: si el hit IDB no decodifica, re-fetch de red
  let image
  let imageId
  try {
    const decoded = await decodeDicomBufferToImage(buffer)
    image = decoded.image
    imageId = decoded.imageId
  } catch (decodeErr) {
    if (fromCache) {
      console.warn(
        'loadDicomInstanceWithCache: decode IDB falló, re-fetch red',
        decodeErr
      )
      const fetched = await fetchDicomBytes(absoluteUrl, {
        headers: extraHeaders,
        credentials: credentials || 'omit',
      })
      buffer = fetched.buffer
      fromCache = false
      if (!buffer || buffer.byteLength === 0) throw decodeErr
      sop = manifestSop || parseSopFromDicomBuffer(buffer) || cacheKey
      const decoded = await decodeDicomBufferToImage(buffer)
      image = decoded.image
      imageId = decoded.imageId
    } else {
      throw decodeErr
    }
  }

  // Si usamos hash temporal y descubrimos SOP real, promover
  if (
    DICOM_OFFLINE_CACHE_ENABLED &&
    !fromCache &&
    !skipIdbWrite &&
    sop &&
    cacheKey.startsWith('urlhash:') &&
    sop !== cacheKey
  ) {
    void deleteCachedKey(cacheKey)
  }

  if (!fromCache && DICOM_OFFLINE_CACHE_ENABLED && sop) {
    schedulePutComplete(sop, studyUid, buffer, skipIdbWrite)
  }

  return {
    image,
    imageId,
    sopInstanceUID: sop,
    fromCache,
  }
}

/**
 * Fallback legacy: wadouri URL directa (sin caché). Auth vía beforeSend del loader.
 */
export async function loadDicomInstanceDirectUrl(absoluteUrl) {
  const imageId = `wadouri:${absoluteUrl}`
  const image = await cornerstone.loadAndCacheImage(imageId)
  return { image, imageId, sopInstanceUID: null, fromCache: false }
}
